import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney } from "../lib/format";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  ArrowLeft, Plus, Trash2, Target, Trophy, CalendarDays, Wallet, Sparkles,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import HelpButton from "../components/HelpButton";

const GOAL_COLORS = ["#b5e4ca", "#fdecc8", "#d9d4f1", "#f6bfc4", "#c9e4ff", "#ffe0b0"];

// Celebrate hitting a savings goal — 2 confetti bursts, teal + gold, no
// dependency on any DOM node so it works even mid-navigation.
function celebrateGoal() {
  const defaults = { spread: 70, startVelocity: 45, gravity: 0.9, ticks: 200, zIndex: 9999 };
  confetti({ ...defaults, particleCount: 90, origin: { x: 0.2, y: 0.7 }, colors: ["#b5e4ca", "#12854a", "#fdecc8", "#c48b00"] });
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 90, origin: { x: 0.8, y: 0.7 }, colors: ["#d9d4f1", "#fdecc8", "#ffb3b3", "#b5e4ca"] });
  }, 250);
}

export default function Goals() {
  const { user } = useAuth();
  const allowed = user?.modules?.savings_goals === true;
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    target_amount: "",
    target_date: "",
    color: GOAL_COLORS[0],
  });
  const [contribAmounts, setContribAmounts] = useState({}); // goal id -> string

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/savings-goals");
      setGoals(data);
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
  }, [allowed]);

  const createGoal = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.target_amount) return;
    try {
      await api.post("/savings-goals", {
        name: form.name.trim(),
        target_amount: parseFloat(form.target_amount),
        target_date: form.target_date || null,
        color: form.color,
      });
      setForm({ name: "", target_amount: "", target_date: "", color: GOAL_COLORS[0] });
      toast.success("Goal created");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    }
  };

  const contribute = async (goal) => {
    const raw = contribAmounts[goal.id];
    const n = parseFloat(raw);
    if (!raw || isNaN(n) || n <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    try {
      const { data } = await api.post(`/savings-goals/${goal.id}/contribute`, { amount: n });
      setContribAmounts({ ...contribAmounts, [goal.id]: "" });
      if (data.achieved_at && !goal.achieved_at) {
        celebrateGoal();
        toast.success(`🎉 ${goal.name} hit its target!`);
      } else {
        toast.success(`Added ${fmtMoney(n)} to ${goal.name}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (goal) => {
    if (!window.confirm(`Delete goal "${goal.name}"? Progress will be lost.`)) return;
    try {
      await api.delete(`/savings-goals/${goal.id}`);
      toast.success("Goal deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (!allowed) {
    return (
      <div className="space-y-4" data-testid="goals-page">
        <div className="flex items-center justify-between">
          <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
            <ArrowLeft size={16} strokeWidth={3} /> Back
          </Link>
          <h1 className="font-display text-2xl font-black">Savings Goals</h1>
          <HelpButton id="goals" />
        </div>
        <div className="nb-card p-6 text-center space-y-3" data-testid="goals-locked">
          <div className="w-14 h-14 mx-auto rounded-full border-2 border-[#1a1a1a] flex items-center justify-center" style={{ background: "#fdecc8" }}>
            <Trophy size={22} strokeWidth={3} />
          </div>
          <div className="font-display font-black text-lg">Savings Goals is a Pro feature</div>
          <div className="text-sm font-medium text-[#4a4a4a]">
            Name a goal, set a target, watch the progress bar fill. Perfect for a car, prom, or your emergency fund.
          </div>
          <Link to="/upgrade" className="nb-btn nb-btn-dark inline-flex !py-2 !px-4">
            Unlock with Pro
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="goals-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Savings Goals</h1>
        <HelpButton id="goals" />
      </div>

      <form onSubmit={createGoal} className="nb-card p-5 space-y-3" data-testid="goal-form">
        <div className="flex items-center gap-2 mb-1">
          <Target size={18} strokeWidth={3} />
          <div className="label !mb-0">New goal</div>
        </div>
        <div>
          <label className="label">Name</label>
          <input
            data-testid="goal-name"
            className="nb-input"
            required
            value={form.name}
            placeholder="Prom dress, iPhone, Emergency fund..."
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Target ($)</label>
            <input
              data-testid="goal-target"
              type="number"
              step="0.01"
              min="1"
              required
              className="nb-input"
              value={form.target_amount}
              placeholder="250.00"
              onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
            />
          </div>
          <div>
            <label className="label">By (optional)</label>
            <input
              data-testid="goal-date"
              type="date"
              className="nb-input"
              value={form.target_date}
              onChange={(e) => setForm({ ...form, target_date: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="label">Color</label>
          <div className="flex gap-2 flex-wrap">
            {GOAL_COLORS.map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => setForm({ ...form, color: c })}
                className="w-8 h-8 rounded-full border-2 border-[#1a1a1a]"
                style={{
                  background: c,
                  boxShadow: form.color === c ? "0 0 0 3px #1a1a1a" : "none",
                }}
                aria-label={`Pick color ${c}`}
              />
            ))}
          </div>
        </div>
        <button type="submit" data-testid="add-goal-btn" className="nb-btn nb-btn-dark w-full !py-3">
          <Plus size={16} strokeWidth={3} /> Create goal
        </button>
      </form>

      {loading && <div className="text-center text-sm font-medium text-[#4a4a4a]">Loading…</div>}

      {!loading && goals.length === 0 && (
        <div className="nb-card p-6 text-center text-sm font-medium text-[#4a4a4a]">
          No goals yet. Add your first above.
        </div>
      )}

      {goals.map((g) => {
        const pct = Math.min(100, (g.current_amount / (g.target_amount || 1)) * 100);
        const remaining = Math.max(0, g.target_amount - g.current_amount);
        const isDone = pct >= 100;
        return (
          <div
            key={g.id}
            className="nb-card p-5 space-y-3"
            style={{ background: g.color || "#b5e4ca" }}
            data-testid={`goal-card-${g.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-black text-lg flex items-center gap-2">
                  {g.name}
                  {isDone && <Trophy size={18} strokeWidth={3} />}
                </div>
                {g.target_date && (
                  <div className="text-xs font-medium text-[#4a4a4a] flex items-center gap-1 mt-0.5">
                    <CalendarDays size={12} strokeWidth={3} />
                    by {g.target_date}
                  </div>
                )}
              </div>
              <button
                data-testid={`delete-goal-${g.id}`}
                onClick={() => remove(g)}
                className="nb-btn nb-btn-secondary !py-1 !px-2 shrink-0"
                title="Delete goal"
              >
                <Trash2 size={14} strokeWidth={3} />
              </button>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm font-bold">
                <span>{fmtMoney(g.current_amount)} / {fmtMoney(g.target_amount)}</span>
                <span>{pct.toFixed(0)}%</span>
              </div>
              <div className="h-4 border-2 border-[#1a1a1a] rounded-full overflow-hidden bg-white">
                <div
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    background: isDone ? "#1a1a1a" : "#1a1a1a",
                  }}
                  data-testid={`goal-progress-${g.id}`}
                />
              </div>
              {!isDone && (
                <div className="text-xs font-medium text-[#4a4a4a]">
                  {fmtMoney(remaining)} to go
                </div>
              )}
            </div>
            {!isDone && (
              <div className="flex gap-2">
                <input
                  data-testid={`contribute-input-${g.id}`}
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Add $ to goal"
                  value={contribAmounts[g.id] || ""}
                  onChange={(e) =>
                    setContribAmounts({ ...contribAmounts, [g.id]: e.target.value })
                  }
                  className="nb-input flex-1 !py-2"
                />
                <button
                  data-testid={`contribute-btn-${g.id}`}
                  onClick={() => contribute(g)}
                  className="nb-btn nb-btn-dark !py-2 !px-3"
                >
                  <Wallet size={14} strokeWidth={3} /> Add
                </button>
              </div>
            )}
            {isDone && (
              <div className="text-sm font-black flex items-center gap-1.5">
                <Sparkles size={14} strokeWidth={3} /> Nailed it — goal reached!
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
